import React, { useState, useRef, useEffect } from "react";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useCreateTag } from "../../hooks/mutations/useCreateTag";
import { Tag } from "../../types";
import { DEFAULT_TAG_COLOR } from "../../constants/defaults";

const TAG_COLORS = [
  { name: "Red", value: "#ef4444" },
  { name: "Orange", value: "#f97316" },
  { name: "Amber", value: "#f59e0b" },
  { name: "Yellow", value: "#eab308" },
  { name: "Lime", value: "#84cc16" },
  { name: "Green", value: "#22c55e" },
  { name: "Emerald", value: "#10b981" },
  { name: "Teal", value: "#14b8a6" },
  { name: "Cyan", value: "#06b6d4" },
  { name: "Sky", value: "#0ea5e9" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Indigo", value: "#6366f1" },
  { name: "Violet", value: "#8b5cf6" },
  { name: "Purple", value: "#a855f7" },
  { name: "Fuchsia", value: "#d946ef" },
  { name: "Pink", value: "#ec4899" },
  { name: "Rose", value: "#f43f5e" },
  { name: "Slate", value: "#64748b" },
];

interface TagPopoverProps {
  tags: Tag[];
  selectedTagIds: string[];
  onToggleTag: (tagId: string) => void;
  onClose: () => void;
  anchorEl: HTMLElement | null;
}

export const TagPopover: React.FC<TagPopoverProps> = ({
  tags,
  selectedTagIds,
  onToggleTag,
  onClose,
  anchorEl,
}) => {
  const createTag = useCreateTag();
  const [showInlineTagCreator, setShowInlineTagCreator] = useState(false);
  const [inlineTagName, setInlineTagName] = useState("");
  const [inlineTagColor, setInlineTagColor] = useState(DEFAULT_TAG_COLOR);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Calculate position based on anchor element
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorEl) {
      // This popover is `position: fixed`, so it's placed in viewport
      // coordinates — getBoundingClientRect() is already viewport-relative,
      // and adding window.scrollX/Y (a document-relative correction meant for
      // `position: absolute`) pushed it off by however far the page happened
      // to be scrolled. Matches FilterPopover / TaskCardMenu, the other fixed
      // popovers anchored this way.
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [anchorEl]);

  // Handle ESC key
  useEscapeKey(onClose);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, anchorEl]);

  const getNextAvailableColor = () => {
    // Get all colors currently used by existing tags
    const usedColors = new Set(tags.map((tag) => tag.color));

    // Find the first color that is not used
    const availableColor = TAG_COLORS.find(
      (color) => !usedColors.has(color.value)
    );

    // If found an available color, use it; otherwise use the default indigo
    return availableColor ? availableColor.value : DEFAULT_TAG_COLOR;
  };

  const handleShowTagCreator = () => {
    setShowInlineTagCreator(true);
    setInlineTagColor(getNextAvailableColor());
  };

  const handleAddInlineTag = async () => {
    if (!inlineTagName.trim()) return;
    try {
      await createTag.mutateAsync({
        name: inlineTagName.trim(),
        color: inlineTagColor,
      });
    } catch {
      // useCreateTag already reports the failure (Sentry + toast). Swallow the
      // throw so the creator still resets and closes, matching the previous
      // addTagToDb adapter, which resolved null on error rather than throwing.
    }
    setInlineTagName("");
    setInlineTagColor(getNextAvailableColor());
    setShowInlineTagCreator(false);
  };

  if (!anchorEl) return null;

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Manage tags"
      className="fixed z-50 bg-warm-bg-card border border-warm-border rounded-lg shadow-xl p-4 min-w-[320px] max-w-[400px] animate-popover-in origin-top-left"
      style={{ top: position.top, left: position.left }}
    >
      {/* Tags List */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 max-h-[200px] overflow-y-auto p-1">
          {tags.map((tag) => {
            const isSelected = selectedTagIds.includes(tag.id);
            return (
              <button
                key={tag.id}
                onClick={() => onToggleTag(tag.id)}
                aria-pressed={isSelected}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  isSelected
                    ? "ring-2 ring-warm-text-primary ring-offset-2 ring-offset-warm-bg-card"
                    : "opacity-60 hover:opacity-100"
                }`}
                style={{ backgroundColor: tag.color, color: "white" }}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Inline Tag Creator */}
      {showInlineTagCreator && (
        <div className="bg-warm-bg-elevated rounded-lg border border-warm-border p-3 space-y-3 mb-3">
          <div className="flex gap-2">
            <div className="relative">
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                aria-label="Choose tag color"
                className="w-10 h-10 rounded-lg border-2 border-warm-border hover:border-warm-text-tertiary transition-colors flex items-center justify-center"
                style={{ backgroundColor: inlineTagColor }}
              >
                <div
                  className="w-6 h-6 rounded-full"
                  style={{ backgroundColor: inlineTagColor }}
                />
              </button>
              {showColorPicker && (
                <div className="absolute top-12 left-0 z-10 bg-warm-bg-card border border-warm-border rounded-lg p-2 shadow-xl w-[200px]">
                  <div className="grid grid-cols-6 gap-1.5">
                    {TAG_COLORS.map((color) => (
                      <button
                        key={color.name}
                        onClick={() => {
                          setInlineTagColor(color.value);
                          setShowColorPicker(false);
                        }}
                        aria-label={color.name}
                        className="w-7 h-7 rounded-full hover:scale-110 transition-transform"
                        style={{ backgroundColor: color.value }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <input
              type="text"
              placeholder="Tag name..."
              value={inlineTagName}
              onChange={(e) => setInlineTagName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddInlineTag();
                }
              }}
              className="flex-1 px-3 py-2 bg-warm-bg-elevated border border-warm-border rounded-lg text-warm-text-primary placeholder:text-warm-text-tertiary focus:outline-none focus:ring-2 focus:ring-warm-blue"
            />
          </div>

          {inlineTagName && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-warm-text-secondary">Preview:</span>
              <span
                className="px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: inlineTagColor, color: "white" }}
              >
                {inlineTagName}
              </span>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowInlineTagCreator(false);
                setInlineTagName("");
                setInlineTagColor(getNextAvailableColor());
              }}
              className="flex-1 px-3 py-2 bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-primary rounded-lg transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleAddInlineTag}
              disabled={!inlineTagName.trim()}
              className="flex-1 px-3 py-2 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Create Tag
            </button>
          </div>
        </div>
      )}

      {/* Add New Tag Button */}
      {!showInlineTagCreator && (
        <button
          onClick={handleShowTagCreator}
          className="w-full text-sm text-warm-blue hover:text-warm-blue-dark dark:hover:text-warm-blue-light transition-colors text-left"
        >
          + Create new tag
        </button>
      )}
    </div>
  );
};
